/**
 * useDashboardActions - Custom Hook for Dashboard Action Handlers
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Manages all dashboard action handlers and callbacks,
 * separating this concern from the main dashboard component.
 * 
 * Responsibilities:
 * - Manage view mode state and handlers
 * - Handle data export operations
 * - Handle time range changes
 * - Provide memoized callbacks for performance
 */

import { useState, useCallback } from 'react';
import { exportToCSV } from '../../../../utils/csv';
import { filterBuoyData } from '../../../../services/dataService';

// Convert a value from a <input type="datetime-local"> (e.g. "2026-04-01T12:30")
// into the absolute ISO string Flux understands ("2026-04-01T12:30:00.000Z").
// Browsers interpret datetime-local as the user's local wall clock; new Date()
// then converts that to UTC, which matches the chart's UTC display.
function localInputToISO(localValue) {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function useDashboardActions(selectedBuoys, rawData, selectedFields) {
  const [viewMode, setViewMode] = useState('graph'); // 'graph' | 'table'
  const [timeRange, setTimeRange] = useState('-7d');
  // timeStop is null for relative presets ("-7d" → stop=now). For custom
  // ranges, both timeRange and timeStop hold absolute ISO timestamps.
  const [timeStop, setTimeStop] = useState(null);
  const [isCustomRange, setIsCustomRange] = useState(false);
  // Hold the raw datetime-local input strings so the inputs stay controlled
  // even before the user has filled both ends.
  const [customStartLocal, setCustomStartLocal] = useState('');
  const [customEndLocal, setCustomEndLocal] = useState('');

  const setGraphView = useCallback(() => setViewMode('graph'), []);
  const setTableView = useCallback(() => setViewMode('table'), []);

  const handleExport = useCallback(() => {
    selectedBuoys.forEach(buoyId => {
      // Use filtered data from the hook (already filtered by buoy and sensor selection)
      const buoyRows = filterBuoyData(rawData, buoyId);
      const fields = selectedFields[buoyId] || [];
      if (buoyRows.length && fields.length) {
        exportToCSV(buoyRows, fields, buoyId);
      }
    });
  }, [selectedBuoys, rawData, selectedFields]);

  const handleTimeRangeChange = useCallback((e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setIsCustomRange(true);
      // Don't fire a query yet — wait until the user picks both ends.
      return;
    }
    setIsCustomRange(false);
    setTimeStop(null);
    setTimeRange(value);
  }, []);

  const applyCustomRange = useCallback((startLocal, endLocal) => {
    const startISO = localInputToISO(startLocal);
    const endISO = localInputToISO(endLocal);
    if (!startISO || !endISO) return;
    if (new Date(startISO) >= new Date(endISO)) return;
    setTimeRange(startISO);
    setTimeStop(endISO);
  }, []);

  const handleCustomStartChange = useCallback((e) => {
    const v = e.target.value;
    setCustomStartLocal(v);
    applyCustomRange(v, customEndLocal);
  }, [customEndLocal, applyCustomRange]);

  const handleCustomEndChange = useCallback((e) => {
    const v = e.target.value;
    setCustomEndLocal(v);
    applyCustomRange(customStartLocal, v);
  }, [customStartLocal, applyCustomRange]);

  return {
    // State
    viewMode,
    timeRange,
    timeStop,
    isCustomRange,
    customStartLocal,
    customEndLocal,

    // Actions
    setGraphView,
    setTableView,
    handleExport,
    handleTimeRangeChange,
    handleCustomStartChange,
    handleCustomEndChange
  };
}